<template>
  <!-- SPACER -->
  <span class="hidden shrink-0 lg:block lg:w-80 xl:w-[22rem] 2xl:w-96" />
  <nav
    id="jump-navigation"
    :aria-expanded="isOpen"
    :aria-label="isOpen ? 'Mobile Navigation is open' : 'hidden'"
    class="
      md:animate-in-left
      fixed left-0 top-0 z-40 flex h-screen w-full max-w-full shrink-0 flex-col bg-surface-300
      p-container-mobile-level-2 text-violet-text
      sm:p-container-desktop-level-1
      lg:w-80
      xl:w-[22rem]
      2xl:w-96
    "
    :class="
      isOpen
        ? 'top-0 duration-500 ease-[cubic-bezier(0.12,0.85,0,0.98)]'
        : 'duration-300 max-lg:top-full'
    "
  >
    <DynLink
      :to="auth.homepage"
      aria-label="hidden"
    >
      <IconLogoAdmin class="w-40" />
    </DynLink>

    <div class="mb-4 mt-[6svh] text-teacher-label">
      Einstellungen
    </div>
    <menu class="flex flex-col">
      <li v-for="{ page } in pages" :key="page.id" class="flex flex-wrap">
        <DynLink
          :to="page.url"
          role="menuitem"
          :aria-label="`to the ${page.title} page`"
          class="
            group -mx-4 my-1 flex w-[calc(100%+2rem)] justify-between rounded-full px-4 py-2 text-lg font-bold
            leading-loose ring-focusTeacher transition-colors duration-300
            hover:text-violet-text-dark hover:duration-75
            focus:outline-none
            focus-visible:bg-surface-500 focus-visible:ring-2
            link-active:bg-surface-500
          "
          @click="isOpen = false"
        >
          {{ page.title }}
          <IconArrowRight class="w-5 -translate-x-2 transition-transform group-hover:translate-x-px" />
        </DynLink>
      </li>
      <li
        v-if="
          auth.staff?.roles.find((role) => role.name === 'teacher')
            && auth.staff?.school_classes.length
        "
        class="-mx-4 mt-8"
      >
        <UiButton
          variant="big"
          :to="{ name: 'Dashboard' }"
          :animate-previous="true"
        >
          {{ $t('general.backToThePlatform') }}
        </UiButton>
      </li>
    </menu>
    <span class="grow" />
    <h2 class="mb-4 typo-label-m">
      HEP-NUTZERPROFIL
    </h2>
    <a
      href="https://www.hep-verlag.ch/profile/details"
      target="_blank"
      class="
        group flex justify-between ring-offset-8
        focus:outline-none
        focus-visible:bg-surface-300 focus-visible:ring-2 focus-visible:ring-focusTeacher
        max-lg:w-fit max-lg:gap-4
      "
    >
      <div class="flex">
        <IconUser
          class="mr-4 w-12 rounded-full bg-violet-500 p-3 text-white group-hover:bg-violet-600"
          aria-label="hidden"
        />
        <div>
          <span
            aria-label="current user name"
            class="text-violet-text text-teacher-text-lg group-hover:text-violet-text-dark"
          >{{ auth.user?.full_name }}</span>
          <div aria-label="current id" class="text-xs leading-none">
            {{ auth.user?.id }}
          </div>
        </div>
      </div>
      <IconArrowUp
        class="w-6 shrink-0 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1"
      />
    </a>
    <div class="mt-[4svh] flex items-center justify-between">
      <button
        class="text-left font-medium text-violet-500 hover:text-violet-600"
        type="button"
        aria-label="log out button"
        @click="auth.logout()"
      >
        {{ $t('general.logOut') }}
      </button>
      <button
        class="group -mr-4 flex h-9 items-center px-4 py-1 text-violet-text-dark transition-opacity lg:hidden"
        type="button"
        @click="isOpen = false"
      >
        <IconCross class="h-full group-hover:scale-y-90" />
      </button>
    </div>
  </nav>

  <!-- CLOSED -->
  <Container
    tag="nav"
    indent="level-1"
    class="
      fixed bottom-0 left-0 z-30 flex w-full items-center justify-between bg-surface-300 py-5 text-violet-text
      lg:hidden
    "
    role="navigation"
  >
    <DynLink
      :to="{ name: 'AdminIndex' }"
      aria-label="Dashboard"
      class="group ring-offset-8 focus:outline-none focus-visible:ring-2 focus-visible:ring-focusTeacher"
    >
      <IconLogoAdmin class="h-8" />
    </DynLink>

    <button
      class="group -mr-4 h-9 px-4 py-1 text-violet-text transition-opacity"
      type="button"
      @click="isOpen = true"
    >
      <IconMenuBurger class="h-full text-violet-text" />
    </button>
  </Container>
</template>

<script lang="ts" setup>
const cms = useCmsStore()
const auth = useAuthStore()

const isOpen = ref(true)
const route = useRoute()

const pages = computed(() =>
  cms.globals?.administration?.tree.filter(({ page }) => {
    return auth.staff?.roles.find(role => page.access.includes(role.name))
  }))

watch(
  () => route.path,
  (oldRoute, newRoute) => {
    if (oldRoute !== '/verwalten' && newRoute !== '/verwalten') {
      isOpen.value = false
    } else {
      isOpen.value = true
    }
  },
)
</script>

<style scoped>
@screen md {
  .md\:animate-in-left {
    animation: animate-in-left 0.5s cubic-bezier(0.12, 0.85, 0.38, 0.93);
  }
  @keyframes animate-in-left {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0);
    }
  }

  .layout-leave-to .md\:animate-in-left {
    @apply -translate-x-full;
  }
}
</style>
